import { useCallback, useEffect, useState } from 'react'

import { asFailure, type Failure } from '../api/request'
import { addSubject, fetchSubjects } from '../api/subjects'
import {
  type Appointment,
  type WantedAppointment,
  addAppointment,
  changeAppointment,
  fetchAppointment,
  fetchAppointments,
  releaseHold,
  removeAppointment,
  repeatAppointment,
  stopRepeating,
} from '../api/timetable'
import { useLoad } from '../api/useLoad'
import { AREA_IDS, ENTRY_AREA_ID } from '../frame/areas'
import { Lanes } from '../frame/Lanes'
import { useLayoutStep } from '../frame/layout'
import { navigate, useRoute } from '../frame/route'
import { useAnnounce } from '../ui/Announce'
import { Button } from '../ui/Control'
import { Failed, Waiting } from '../ui/Status'
import { QuickAppointment, type Wanted } from './timetable/QuickAppointment'
import { type Repetition, RepeatFields, defaultUntil, weekdayOfDay } from './timetable/RepeatFields'
import { Calendars } from './timetable/Calendars'
import { Peek } from './timetable/Peek'
import { AppointmentSheet } from './timetable/AppointmentSheet'
import {
  openedIn,
  pathToDay,
  pathToCalendars,
  pathToAppointment,
  pathToNew,
  pathToWeek,
  TIMETABLE_AREA,
} from './timetable/place'
import {
  dayFrom,
  dayKey,
  daysOf,
  daySpan,
  hereZone,
  minutesOf,
  momentAt,
  readableDay,
  readableWeek,
  todayKey,
  weekFrom,
  weekOf,
  weekSpan,
} from './timetable/week'
import { DayInReview } from './timetable/DayInReview'
import { Agenda } from './timetable/Agenda'
import { type Draft, Grid } from './timetable/Grid'

/**
 * The timetable: what is on, and what belongs to each of it.
 *
 * Two things carry the whole area.
 *
 * **Where you are is in the address.** `#/timetable/week/2026-08-17` is a week and
 * `#/timetable/day/2026-08-17` is a day, so a link to a week is a link somebody can send and a
 * reload lands where they were rather than on today. Paging is visible buttons, never only a swipe.
 *
 * **An appointment is an anchor.** It keeps its id through every reconciliation, past terms are kept for
 * ever, and nothing here removes one by age — which is what makes it something material, notes and
 * cards can hang off later.
 *
 * On a phone the day is what opens, with a strip of the week above it to jump by; the week becomes a
 * list grouped by day rather than a shrunken grid, because seven columns on 390 points is not a
 * smaller grid but an unreadable one.
 */
export function Timetable() {
  const route = useRoute(AREA_IDS, ENTRY_AREA_ID)
  const opened = openedIn(route.path)
  const announce = useAnnounce()
  const step = useLayoutStep()

  const monday = weekOf(opened.day)

  /*
   * What is fetched depends on the address and on nothing else.
   *
   * The two segments are read **straight out of the route** rather than off the object above, and
   * the period is worked out inside the callback. Both halves matter: a `Date` built beside the
   * loader would never equal itself on the next render, and a value taken off a locally built object
   * is exactly what the compiler cannot vouch for.
   */
  const viewSegment = route.path[0]
  const daySegment = route.path[1]
  const appointments = useLoad(
    useCallback(
      (signal: AbortSignal) => {
        const there = openedIn([viewSegment ?? '', daySegment ?? ''])
        const span = there.view === 'week' ? weekSpan(weekOf(there.day)) : daySpan(there.day)
        return fetchAppointments(span.from, span.until, null, signal)
      },
      [viewSegment, daySegment],
    ),
  )
  const subjects = useLoad(useCallback((signal: AbortSignal) => fetchSubjects(false, signal), []))
  const [refused, setRefused] = useState<Failure | null>(null)
  /*
   * The stretch somebody has drawn but not yet named.
   *
   * It is held here and **not written**: a drag thought better of leaves nothing behind. The appointment
   * comes into being when the little sheet is saved, and not a moment earlier.
   */
  const [draft, setDraft] = useState<Draft | null>(null)
  /** The appointment a single click asked about, and where it was asked. */
  const [peeked, setPeeked] = useState<{
    appointment: Appointment
    at: { x: number; y: number }
  } | null>(null)

  const all: readonly Appointment[] =
    appointments.load.status === 'ready' ? appointments.load.value : []
  const known = subjects.load.status === 'ready' ? subjects.load.value : []
  const open = all.find((appointment) => appointment.id === opened.appointmentId)

  /*
   * An appointment that is open while the address does not say which week it is on.
   *
   * This is what arriving from a link or from a subject looks like. Both know a kind and an id and
   * deliberately nothing else, so they can only write "this appointment, week unknown" — and left as
   * it stands that address shows the current week, with the lecture that is in November nowhere on it.
   * One request settles when the appointment is, and the address left standing afterwards carries the
   * week: which is what makes it sendable, and what the sheet closes back to.
   *
   * An appointment nothing can be said about — removed, or never there — corrects nothing: staying put
   * is more honest than moving somebody on a guess.
   */
  const ready = appointments.load.status === 'ready'
  const openId = opened.appointmentId
  /** Whether the day in the address is one: `openedIn` falls back to today where it is not. */
  const guessed = daySegment !== opened.day
  useEffect(() => {
    if (openId === null || !ready) {
      return
    }
    const correct = (appointment: Appointment) => {
      const day = dayKey(new Date(appointment.startsAt), appointment.zone)
      navigate(TIMETABLE_AREA, pathToAppointment(opened.view, day, openId, opened.tab), {
        replace: true,
      })
    }
    // Already on the week: the day is known without asking anybody, and only the address is behind.
    if (open !== undefined) {
      if (guessed) {
        correct(open)
      }
      return
    }
    const asking = new AbortController()
    void fetchAppointment(openId, asking.signal)
      .then((appointment) => {
        if (guessed || dayKey(new Date(appointment.startsAt), appointment.zone) !== opened.day) {
          correct(appointment)
        }
      })
      .catch(() => {
        /* Nothing to correct it to. */
      })
    return () => {
      asking.abort()
    }
  }, [openId, open, ready, guessed, opened.day, opened.view, opened.tab])

  const go = (path: string[]) => {
    navigate(TIMETABLE_AREA, path)
  }

  /** The plan as it stands, with nothing open over it — where every sheet closes back to. */
  const here = () => (opened.view === 'week' ? pathToWeek(opened.day) : pathToDay(opened.day))

  /**
   * Writes down what was drawn on the grid.
   *
   * The zone is the one the reader is standing in, because that is where the drag happened — and it
   * is kept at the appointment, so the hour stays that hour for everybody afterwards.
   */
  const writeDown = (what: Wanted, thenOpen: boolean) => {
    void attempt(async () => {
      const made = await addAppointment({
        title: what.title,
        startsAt: what.startsAt,
        endsAt: what.endsAt,
        allDay: what.allDay,
        zone: hereZone(),
      })
      /*
        A repetition asked for while writing it down is the same act, one request later: the rule is
        built out of the appointment, so the appointment has to exist first.
      */
      if (what.repetition !== null) {
        await repeatAppointment(
          made.id,
          what.repetition.everyWeeks,
          what.repetition.weekdays,
          what.repetition.until,
        )
      }
      setDraft(null)
      appointments.retry()
      announce('Written down.')
      if (thenOpen) {
        go(pathToAppointment(opened.view, opened.day, made.id))
      }
    })
  }

  /**
   * An appointment dragged to another hour, another day, or taken longer by its edge.
   *
   * One save for all three, and it goes through the ordinary change — so the two times become a hand
   * correction and no source overwrites them again. That is not a side effect: somebody who moves a
   * lecture by hand means it.
   */
  const moved = (appointment: Appointment, from: number, to: number, day: string) => {
    void attempt(async () => {
      await changeAppointment(appointment.id, {
        startsAt: momentAt(day, from, appointment.zone).toISOString(),
        endsAt: momentAt(day, to, appointment.zone).toISOString(),
      })
      appointments.retry()
      announce('Moved.')
    })
  }

  const attempt = async (work: () => Promise<void>) => {
    try {
      setRefused(null)
      await work()
    } catch (error) {
      setRefused(asFailure(error))
    }
  }

  const detail = (() => {
    if (opened.subscribing) {
      return {
        label: 'Calendars',
        content: (
          <Calendars
            onChanged={() => {
              appointments.retry()
              announce('Calendar read.')
            }}
            onClose={() => {
              go(here())
            }}
          />
        ),
      }
    }
    if (opened.writing) {
      return {
        label: 'A new appointment',
        content: (
          <NewAppointment
            day={opened.day}
            onCreate={(wanted, repetition) => {
              void attempt(async () => {
                const made = await addAppointment(wanted)
                if (repetition !== null) {
                  await repeatAppointment(
                    made.id,
                    repetition.everyWeeks,
                    repetition.weekdays,
                    repetition.until,
                  )
                }
                appointments.retry()
                announce('Written down.')
                go(pathToAppointment(opened.view, opened.day, made.id))
              })
            }}
            onClose={() => {
              go(here())
            }}
          />
        ),
      }
    }
    if (open !== undefined) {
      return {
        label: 'Appointment',
        content: (
          <AppointmentSheet
            // Keyed by what the service last said: a change arriving is a new surface rather than
            // one being written into from outside.
            key={`${open.id}-${open.updatedAt}`}
            appointment={open}
            subjects={known}
            onChange={(wanted, scope) => {
              void attempt(async () => {
                await changeAppointment(open.id, wanted, scope)
                appointments.retry()
                announce('Saved.')
              })
            }}
            onRelease={(fact) => {
              void attempt(async () => {
                await releaseHold(open.id, fact)
                appointments.retry()
                announce('The source decides this again.')
              })
            }}
            onCreateSubject={(name) => {
              void attempt(async () => {
                const made = await addSubject(name)
                await changeAppointment(open.id, { subjectId: made.id })
                subjects.retry()
                appointments.retry()
                announce('Subject created.')
              })
            }}
            onRepeat={(repetition) => {
              void attempt(async () => {
                const made = await repeatAppointment(
                  open.id,
                  repetition.everyWeeks,
                  repetition.weekdays,
                  repetition.until,
                )
                appointments.retry()
                announce(`${String(made.appointments.length)} appointments.`)
              })
            }}
            onStopRepeating={() => {
              void attempt(async () => {
                await stopRepeating(open.id)
                appointments.retry()
                announce('It no longer repeats.')
              })
            }}
            onRemove={() => {
              void attempt(async () => {
                await removeAppointment(open.id)
                appointments.retry()
                go(here())
                announce('Deleted.')
              })
            }}
            /*
              Something was written or linked, so the plan reads itself again: what the day shows of a
              session is the first line of its write-up and how much hangs on it, and both travel with
              the appointment.

              The sheet is keyed by `updatedAt`, which a write-up does not move — writing about a
              session does not change the session — so this reload replaces the list under an open
              sheet without remounting it, and the cursor stays where it was.
            */
            tab={opened.tab}
            onTab={(tab) => {
              go(pathToAppointment(opened.view, opened.day, open.id, tab))
            }}
            onSessionChanged={() => {
              appointments.retry()
            }}
            onClose={() => {
              go(here())
            }}
          />
        ),
      }
    }
    return null
  })()

  /*
    Which drawing the timetable gets, by the one layout rule the whole frame runs on.

    `compact` and not a width written here: the step is where MyLib already decides that there is
    room for one lane and the areas belong at the bottom, and a second threshold of its own would be
    a second answer to the same question.
  */
  const onAPhone = step === 'compact'
  const shownDays = opened.view === 'week' ? daysOf(monday) : [opened.day]

  return (
    <Lanes
      overview={{
        label: 'Timetable',
        content: (
          <div className="timetable">
            <header className="timetable__head">
              <h2 className="timetable__title">
                {opened.view === 'week' ? readableWeek(monday) : readableDay(opened.day)}
              </h2>
              <div className="controls controls--tight">
                {/* Visible controls, always. A swipe may be quicker; it is never the only way. */}
                <Button
                  tone="quiet"
                  icon="back"
                  iconOnlyLabel={opened.view === 'week' ? 'Week before' : 'Day before'}
                  onClick={() => {
                    go(
                      opened.view === 'week'
                        ? pathToWeek(weekFrom(monday, -1))
                        : pathToDay(dayFrom(opened.day, -1)),
                    )
                  }}
                />
                <Button
                  tone="quiet"
                  onClick={() => {
                    go(opened.view === 'week' ? pathToWeek(todayKey()) : pathToDay(todayKey()))
                  }}
                >
                  Today
                </Button>
                <Button
                  tone="quiet"
                  icon="details"
                  iconOnlyLabel={opened.view === 'week' ? 'Week after' : 'Day after'}
                  onClick={() => {
                    go(
                      opened.view === 'week'
                        ? pathToWeek(weekFrom(monday, 1))
                        : pathToDay(dayFrom(opened.day, 1)),
                    )
                  }}
                />
              </div>
            </header>

            <div className="timetable__ways">
              <div className="controls controls--tight">
                <Button
                  tone={opened.view === 'week' ? 'primary' : 'bare'}
                  aria-pressed={opened.view === 'week'}
                  onClick={() => {
                    go(pathToWeek(opened.day))
                  }}
                >
                  Week
                </Button>
                <Button
                  tone={opened.view === 'day' ? 'primary' : 'bare'}
                  aria-pressed={opened.view === 'day'}
                  onClick={() => {
                    go(pathToDay(opened.day))
                  }}
                >
                  Day
                </Button>
              </div>
              <div className="controls controls--tight">
                {/*
                  The loudest control on the screen, and deliberately: writing an appointment down is
                  what somebody comes here to do. The grid is the quicker way and the button is the
                  one that can be seen — so it is the one that looks like the way in.
                */}
                <Button
                  tone="primary"
                  icon="plus"
                  onClick={() => {
                    go(pathToNew(opened.view, opened.day))
                  }}
                >
                  Add an appointment
                </Button>
                <Button
                  tone="quiet"
                  onClick={() => {
                    go(pathToCalendars(opened.view, opened.day))
                  }}
                >
                  Calendars
                </Button>
              </div>
            </div>

            {/* On a phone the week is reached day by day through this strip. */}
            {opened.view === 'day' && (
              <nav className="timetable__strip" aria-label="The days of this week">
                {daysOf(monday).map((day) => (
                  <button
                    type="button"
                    key={day}
                    className="timetable__strip-day"
                    aria-current={day === opened.day}
                    onClick={() => {
                      go(pathToDay(day))
                    }}
                  >
                    {readableDay(day)}
                  </button>
                ))}
              </nav>
            )}

            {refused !== null && <Failed failure={refused} />}

            {appointments.load.status === 'waiting' && <Waiting label="Fetching your timetable." />}
            {appointments.load.status === 'failed' && (
              <Failed
                failure={appointments.load.failure}
                onRetry={appointments.retry}
                title="The timetable did not load"
              />
            )}

            {/*
              One drawing for a week and a day alike — a day is a week with one column, so nothing
              about the placement can drift apart between them.

              Which drawing is the phone question, and it is answered here rather than in the
              stylesheet. A time grid on 390 points is not a smaller time grid: seven columns are
              48 points wide, an hour is a few points tall, and dragging a 15-minute raster with a
              thumb is a gesture nobody lands. So a phone gets a list of days with a shape of its
              own — see `Agenda` — instead of a grid with its columns taken away by a media query.
            */}
            {onAPhone ? (
              <Agenda
                days={shownDays}
                appointments={all}
                openId={opened.appointmentId}
                onOpen={(id) => {
                  go(pathToAppointment(opened.view, opened.day, id))
                }}
                onAdd={(day) => {
                  go(pathToNew(opened.view, day))
                }}
              />
            ) : (
              <Grid
                days={shownDays}
                appointments={all}
                openId={opened.appointmentId}
                onOpen={(id) => {
                  go(pathToAppointment(opened.view, opened.day, id))
                }}
                onPeek={(appointment, at) => {
                  setPeeked({ appointment, at })
                }}
                onDraw={(drawn) => {
                  setPeeked(null)
                  setDraft(drawn)
                }}
                onMoved={moved}
              />
            )}

            {/*
              The day read back, under the grid and only in the day view. The grid answers "when is
              what"; this answers "what happened", and the two are the same day seen from two sides —
              which is why the diary is here and not in an area of its own.
            */}
            {opened.view === 'day' && (
              <DayInReview
                appointments={all.filter((one) => onThisDay(one, opened.day))}
                onOpen={(id) => {
                  go(pathToAppointment(opened.view, opened.day, id))
                }}
              />
            )}

            {peeked !== null && (
              <Peek
                appointment={peeked.appointment}
                at={peeked.at}
                onOpen={() => {
                  const which = peeked.appointment.id
                  setPeeked(null)
                  go(pathToAppointment(opened.view, opened.day, which))
                }}
                onRemove={() => {
                  const which = peeked.appointment.id
                  setPeeked(null)
                  void attempt(async () => {
                    await removeAppointment(which)
                    appointments.retry()
                    announce('Deleted.')
                  })
                }}
                onClose={() => {
                  setPeeked(null)
                }}
              />
            )}

            {draft !== null && (
              <QuickAppointment
                draft={draft}
                onCreate={(what) => {
                  writeDown(what, false)
                }}
                onMore={(what) => {
                  writeDown(what, true)
                }}
                onClose={() => {
                  setDraft(null)
                }}
              />
            )}
          </div>
        ),
      }}
      detail={detail}
      /*
        The detail is a whole page, not a second column.
        
        Asked for by the area rather than decided by the frame — the same mechanism the library uses
        for writing. What stands on an appointment is going to grow: the note taken in that session,
        the material that belongs to it, the cards made from it. None of that fits in a lane beside a
        grid, and the way back is the frame's own control.
      */
      full={detail !== null}
    />
  )
}

/**
 * An appointment entered by hand.
 *
 * The always-working path, and therefore the plainest surface in the area: a title, a day and two
 * times. Nothing about a source, nothing about a series, nothing to decide first.
 */
function NewAppointment({
  day,
  onCreate,
  onClose,
}: {
  readonly day: string
  readonly onCreate: (wanted: WantedAppointment, repetition: Repetition | null) => void
  readonly onClose: () => void
}) {
  const [title, setTitle] = useState('')
  const [on, setOn] = useState(day)
  const [lastDay, setLastDay] = useState(day)
  const [from, setFrom] = useState('08:00')
  const [to, setTo] = useState('09:30')
  const [room, setRoom] = useState('')

  const [allDay, setAllDay] = useState(false)
  const [severalDays, setSeveralDays] = useState(false)
  const [repeats, setRepeats] = useState(false)
  const [repetition, setRepetition] = useState<Repetition>(() => ({
    weekdays: [weekdayOfDay(day)],
    everyWeeks: 1,
    until: defaultUntil(day),
  }))

  const until = severalDays ? lastDay : on
  const ready = title.trim() !== '' && on !== '' && until >= on

  return (
    <article
      className="sheet sheet--appointment"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onClose()
        }
      }}
    >
      <header className="sheet__head">
        <Button
          tone="bare"
          className="sheet__close"
          icon="close"
          iconOnlyLabel="Close"
          onClick={onClose}
        />
        <h2 className="sheet__title-text">An appointment</h2>
      </header>

      <div className="appointment-detail">
        <div className="appointment-detail__field">
          <label className="appointment-detail__label" htmlFor="new-appointment-title">
            Title
          </label>
          <input
            id="new-appointment-title"
            className="appointment-detail__text"
            value={title}
            placeholder="Study group, exam, a lecture that is in no system"
            onChange={(event) => {
              setTitle(event.target.value)
            }}
          />
        </div>
        <div className="appointment-detail__times">
          <div className="appointment-detail__field">
            <label className="appointment-detail__label" htmlFor="new-appointment-day">
              {severalDays ? 'First day' : 'Day'}
            </label>
            <input
              id="new-appointment-day"
              type="date"
              className="appointment-detail__text"
              value={on}
              onChange={(event) => {
                setOn(event.target.value)
              }}
            />
          </div>
          {severalDays && (
            <div className="appointment-detail__field">
              <label className="appointment-detail__label" htmlFor="new-appointment-last-day">
                Last day
              </label>
              <input
                id="new-appointment-last-day"
                type="date"
                className="appointment-detail__text"
                value={lastDay}
                onChange={(event) => {
                  setLastDay(event.target.value)
                }}
              />
            </div>
          )}
        </div>
        {!allDay && (
          <div className="appointment-detail__times">
            <div className="appointment-detail__field">
              <label className="appointment-detail__label" htmlFor="new-appointment-from">
                From
              </label>
              <input
                id="new-appointment-from"
                type="time"
                className="appointment-detail__text"
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value)
                }}
              />
            </div>
            <div className="appointment-detail__field">
              <label className="appointment-detail__label" htmlFor="new-appointment-to">
                To
              </label>
              <input
                id="new-appointment-to"
                type="time"
                className="appointment-detail__text"
                value={to}
                onChange={(event) => {
                  setTo(event.target.value)
                }}
              />
            </div>
          </div>
        )}

        {/* The rarer shapes, one chip each — the same three as in the little sheet on the grid. */}
        <div className="quick__more" role="group" aria-label="How long it lasts">
          <button
            type="button"
            className="chip"
            aria-pressed={allDay}
            onClick={() => {
              setAllDay((before) => !before)
            }}
          >
            All day
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={severalDays}
            onClick={() => {
              setSeveralDays((before) => {
                if (!before) {
                  setLastDay(on)
                }
                return !before
              })
            }}
          >
            Several days
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={repeats}
            onClick={() => {
              setRepeats((before) => !before)
            }}
          >
            Repeats
          </button>
        </div>

        {repeats && <RepeatFields value={repetition} onChange={setRepetition} />}
        <div className="appointment-detail__field">
          <label className="appointment-detail__label" htmlFor="new-appointment-room">
            Room
          </label>
          <input
            id="new-appointment-room"
            className="appointment-detail__text"
            value={room}
            onChange={(event) => {
              setRoom(event.target.value)
            }}
          />
        </div>

        <div className="controls">
          <Button
            tone="primary"
            disabled={!ready}
            onClick={() => {
              /*
                The two times are sent as moments in the reader's own zone — which is where they
                were typed. The service keeps the zone beside them, so the appointment stays at 8:00 for
                everybody afterwards.
              */
              onCreate(
                {
                  title,
                  startsAt: momentAt(on, allDay ? 0 : minutesOf(from), hereZone()).toISOString(),
                  endsAt: allDay
                    ? momentAt(dayFrom(until, 1), 0, hereZone()).toISOString()
                    : momentAt(until, minutesOf(to), hereZone()).toISOString(),
                  allDay,
                  zone: hereZone(),
                  room,
                },
                repeats ? repetition : null,
              )
            }}
          >
            Write it down
          </Button>
        </div>
      </div>
    </article>
  )
}

/**
 * Whether this appointment falls on that day, read in the zone it was **spoken in**.
 *
 * Its own zone and not the reader's: an 8:00 lecture belongs to its Tuesday wherever somebody is
 * reading the plan from, which is the same rule the grid places it by.
 */
function onThisDay(
  appointment: { readonly startsAt: string; readonly zone: string },
  day: string,
): boolean {
  return dayKey(new Date(appointment.startsAt), appointment.zone) === day
}
